import React, { useState, useEffect, useMemo, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { tripsAPI, trucksAPI, driversAPI, operationsAPI } from '../lib/api';
import { fetchAllPages } from '../lib/fetchAllPages';
import { lotsAPI } from '../lib/lotsApi';
import { AuthContext } from '../context/AuthContext';
import { Loader2, Check, AlertCircle } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { SearchableSelect } from '../components/ui/searchable-select';
import { driverOption, truckOption, reperePropriete } from '../lib/selectOptions';
import { useVerificationFlotte } from '../hooks/useVerificationFlotte';
import { formatVolume, formatDate } from '../lib/utils';
import { toast } from '../components/ui/sonner';
import { messageErreur } from '../lib/erreurs';
import { nomLisible, texteSur } from '../lib/libelles';
import { FieldError } from '../components/ui/field-error';
import { PageHeader } from '../components/ui/page-header';
import { LoadingState } from '../components/ui/loading-state';
import { FormActions } from '../components/ui/page-layouts';
import ChampsAvances from '../components/ui/champs-avances';
import { usePrixPropose, appliquerPrixPropose } from '../hooks/usePrixPropose';

const NewTripPage = () => {
  const navigate = useNavigate();

  const [trucks, setTrucks] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [operations, setOperations] = useState([]);
  // Lots attribués ouverts (lot 057) : un trajet appartient à un convoi OU à un lot.
  const [lots, setLots] = useState([]);
  const auth = useContext(AuthContext);
  const peutChoisirUnLot = Boolean(auth && typeof auth.hasModule === 'function' && auth.hasModule('lots'));
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [submitAttempted, setSubmitAttempted] = useState(false);

  // Ni pays, ni date de départ, ni prix: les trois se déduisent. Les pays
  // venaient du corridor du convoi et étaient pourtant écrits en dur ici; la
  // date était affichée sous le sélecteur de convoi puis redemandée; le prix
  // valait `725` quoi qu'en dise le barème de la société.
  const [formData, setFormData] = useState({
    operation_id: '',
    lot_id: '',
    truck_id: '',
    driver_id: '',
    return_date: '',
    fuel_quantity_estimated: '',
    fuel_price_unit: '',
  });
  // Le barème cesse de renseigner le champ dès que quelqu'un y touche: un
  // tarif de référence ne doit jamais écraser un prix négocié.
  const [prixTouche, setPrixTouche] = useState(false);

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadData = async () => {
    try {
      setIsLoading(true);
      const [trucksPage, driversPage, operationsPage, lotsOuverts] = await Promise.all([
        fetchAllPages((page) => trucksAPI.getAllPaged(page)),
        fetchAllPages((page) => driversAPI.getAllPaged(page)),
        fetchAllPages((page) => operationsAPI.getAllPaged(page)),
        // Les lots sont un confort : s'ils manquent, le choix du convoi reste.
        peutChoisirUnLot ? lotsAPI.list({ status: 'OUVERT' }).catch(() => []) : Promise.resolve([]),
      ]);

      setTrucks(
        (trucksPage.items || []).filter((t) => t.current_status === 'FREE')
      );
      setDrivers((driversPage.items || []).filter((d) => d.active));
      setOperations((operationsPage.items || []).filter((o) => o.active));
      setLots(lotsOuverts);
    } catch (err) {
      console.error('Failed to load data:', err);
      setError('Impossible de charger les camions, chauffeurs et convois. Réessayez.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitAttempted(true);

    if ((!formData.operation_id && !formData.lot_id) || !formData.truck_id) {
      const message = lots.length > 0
        ? 'Veuillez sélectionner un convoi ou un lot, et un camion'
        : 'Veuillez sélectionner un convoi et un camion';
      setError(message);
      toast.error(lots.length > 0 ? 'Choisissez un convoi ou un lot, et un camion' : 'Choisissez un convoi et un camion');
      return;
    }
    if (truckReturned) {
      const raison = fleetCheck.raison(formData.truck_id);
      setError(raison);
      toast.error(raison);
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      // On n'envoie que ce qui a été décidé. Une clé absente laisse le serveur
      // poser sa valeur — les pays depuis le corridor, la date depuis le
      // convoi, le chauffeur depuis le camion. Une clé à `null` l'en empêche.
      const tripData = { truck_id: formData.truck_id };
      if (formData.operation_id) tripData.operation_id = formData.operation_id;
      if (formData.lot_id) tripData.lot_id = formData.lot_id;
      if (formData.driver_id && formData.driver_id !== 'none_selected') {
        tripData.driver_id = formData.driver_id;
      }
      if (formData.fuel_quantity_estimated) {
        tripData.fuel_quantity_estimated = parseFloat(formData.fuel_quantity_estimated);
      }
      if (formData.fuel_price_unit) {
        tripData.fuel_price_unit = parseFloat(formData.fuel_price_unit);
      }
      if (formData.return_date) tripData.return_date = formData.return_date;

      const result = await tripsAPI.create(tripData, { corridorId });
      toast.success('Trajet créé');
      navigate(`/trips/${result.id}`);
    } catch (err) {
      console.error('Failed to create trip:', err);
      const msg = messageErreur(err, 'Le trajet n’a pas pu être créé');
      setError(msg);
      toast.error(msg);
    } finally {
      setIsSubmitting(false);
    }
  };

  const missingOperation = submitAttempted && !formData.operation_id && !formData.lot_id;
  const missingTruck = submitAttempted && !formData.truck_id;

  const selectedTruck = trucks.find((t) => t.id === formData.truck_id);
  const selectedOperation = operations.find(
    (o) => o.id === formData.operation_id
  );

  const lotOptions = useMemo(
    () => [
      { value: '', label: 'Aucun lot' },
      ...lots.map((lot) => ({
        value: lot.id,
        label: nomLisible(lot.reference, 'Lot sans référence'),
        description: lot.transporter_name ? texteSur(lot.transporter_name, '') : undefined,
        keywords: [lot.transporter_name, lot.product],
      })),
    ],
    [lots]
  );

  const operationOptions = useMemo(
    () =>
      operations.map((op) => ({
        value: op.id,
        label: nomLisible(op.code, 'Convoi sans nom'),
        description: op.route ? texteSur(op.route, '') : undefined,
      })),
    [operations]
  );
  // Un camion loué dont le contrat est terminé à la date de départ ne peut plus
  // partir: on le grise avec la raison, et on bloque l'enregistrement s'il est
  // déjà choisi. Seul le contrôle du serveur fait foi; ici on prévient avant.
  const fleetCheck = useVerificationFlotte({
    camionsIds: trucks.filter((t) => t.ownership_type !== 'PROPRE').map((t) => t.id),
    date: selectedOperation?.departure_date,
  });
  const truckReturned = Boolean(formData.truck_id) && fleetCheck.estRendu(formData.truck_id);

  const truckOptions = useMemo(
    () =>
      trucks.map((truck) => {
        const rendu = fleetCheck.estRendu(truck.id);
        return {
          ...truckOption(truck),
          disabled: rendu,
          description: [
            formatVolume(truck.capacity_liters),
            truck.driver_full_name,
            reperePropriete(truck),
            rendu ? fleetCheck.raison(truck.id) : null,
          ]
            .filter(Boolean)
            .join(' · '),
        };
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [trucks, fleetCheck.verification]
  );
  const driverOptions = useMemo(
    () => [
      { value: 'none_selected', label: 'Chauffeur habituel du camion' },
      ...drivers.map(driverOption),
    ],
    [drivers]
  );

  // Le corridor du convoi, transmis à la création. C'est lui qui porte les
  // deux pays et qui permet de calculer l'itinéraire — et il est plus précis
  // que le couple de pays, puisque Dakar compte pour deux corridors.
  const corridorId =
    selectedOperation?.scheduled_trips?.corridor_id ||
    selectedOperation?.corridor_id ||
    undefined;

  const { prix, nomBareme, aucunBareme } = usePrixPropose({
    operationType: 'TRIP_FUEL',
    actif: Boolean(formData.operation_id),
  });

  useEffect(() => {
    setFormData((prev) => {
      const suite = appliquerPrixPropose({
        prix,
        valeurActuelle: prev.fuel_price_unit,
        toucheParLHumain: prixTouche,
      });
      return suite === prev.fuel_price_unit
        ? prev
        : { ...prev, fuel_price_unit: suite };
    });
  }, [prix, prixTouche]);

  // La citerne du camion était affichée à côté du sélecteur sans jamais servir
  // de valeur: l'exploitant la lisait, puis la retapait dans le champ suivant.
  useEffect(() => {
    if (!selectedTruck?.capacity_liters) return;
    setFormData((prev) =>
      prev.fuel_quantity_estimated
        ? prev
        : { ...prev, fuel_quantity_estimated: String(selectedTruck.capacity_liters) },
    );
  }, [selectedTruck]);

  if (isLoading) {
    return (
      <div className="mx-auto w-full max-w-page space-y-5 px-4 py-5 md:px-6 md:py-6 lg:px-8">
        <LoadingState shape="detail" />
      </div>
    );
  }

  return (
    <div className="mx-auto w-full max-w-3xl space-y-5 px-4 py-5 md:px-6 md:py-6 lg:px-8" data-testid="new-trip-page">
      {/* The title was buried in a card header, so this screen was the only
          one whose name did not sit where every other screen puts it. */}
      <PageHeader
        eyebrow="Transport"
        title="Nouveau trajet"
        backTo="/trips"
        backLabel="Retour aux trajets"
      />

      <Card className="card-base">
        <CardContent className="pt-4">
          {error && (
            <div className="flex items-center gap-2 p-4 mb-6 bg-destructive/10 border border-destructive/30 rounded-lg text-destructive">
              <AlertCircle className="w-5 h-5" />
              <span>{error}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Trois champs. Il y en avait douze, et le formulaire n'en
                exigeait que trois — les neuf autres, l'écran les devinait ou
                les demandait trop tôt.

                Les deux pays étaient écrits en dur dans cette page (`'Sénégal'`
                et `'Mali'`) alors que le corridor du convoi les porte. La date
                de départ était redemandée alors qu'elle s'affiche sous le
                sélecteur de convoi. Et la consommation, la conso/espèces et les
                frais de route étaient réclamés à la création, alors que
                l'en-tête de l'ancienne étape 3 annonçait lui-même que « les
                valeurs réelles se saisissent au retour »: on demandait à
                l'exploitant de prédire ce qu'il constaterait six jours plus
                tard. Ils vivent maintenant sur la fiche du trajet. */}
            <div className="space-y-4">
              <div>
                <Label className="label-base">{lots.length > 0 ? 'Convoi' : 'Convoi *'}</Label>
                <SearchableSelect
                  className="input-base"
                  data-testid="operation-select"
                  placeholder="Sélectionner un convoi"
                  searchPlaceholder="Rechercher un convoi…"
                  emptyMessage={
                    operations.length === 0
                      ? 'Aucun convoi disponible'
                      : 'Aucun convoi trouvé'
                  }
                  value={formData.operation_id || ''}
                  onValueChange={(v) => handleChange('operation_id', v)}
                  options={operationOptions}
                />
                <FieldError show={missingOperation} className="text-xs">
                  Champ requis
                </FieldError>
                {/* Ce que le convoi apporte, dit une fois: l'exploitant voit
                    ce qu'il n'a pas à saisir plutôt que de le retaper. */}
                {selectedOperation && (
                  <p className="mt-1 text-sm text-muted-foreground">
                    Départ le{' '}
                    {selectedOperation.departure_date
                      ? formatDate(selectedOperation.departure_date)
                      : '—'}
                    {selectedOperation.route ? ` · ${texteSur(selectedOperation.route, '')}` : ''}
                  </p>
                )}
              </div>

              {lots.length > 0 ? (
                <div>
                  <Label className="label-base">Lot</Label>
                  <SearchableSelect
                    className="input-base"
                    data-testid="lot-select"
                    placeholder="Sélectionner un lot"
                    searchPlaceholder="Rechercher un lot…"
                    emptyMessage="Aucun lot trouvé"
                    value={formData.lot_id || ''}
                    onValueChange={(v) => handleChange('lot_id', v)}
                    options={lotOptions}
                  />
                </div>
              ) : null}

              <div>
                <Label className="label-base">Camion *</Label>
                <SearchableSelect
                  className="input-base"
                  data-testid="truck-select"
                  placeholder="Sélectionner un camion"
                  searchPlaceholder="Rechercher une immatriculation…"
                  emptyMessage={
                    trucks.length === 0
                      ? 'Aucun camion disponible'
                      : 'Aucun camion trouvé'
                  }
                  value={formData.truck_id || ''}
                  onValueChange={(v) => handleChange('truck_id', v)}
                  options={truckOptions}
                />
                <FieldError show={missingTruck} className="text-xs">
                  Champ requis
                </FieldError>
                {truckReturned && (
                  <div
                    role="alert"
                    className="mt-2 flex items-start gap-2 rounded-lg border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive"
                    data-testid="truck-returned-warning"
                  >
                    <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
                    <span>
                      <span className="block font-medium">
                        {fleetCheck.raison(formData.truck_id)}
                      </span>
                      <span className="block">
                        Choisissez un autre camion pour enregistrer ce trajet.
                      </span>
                    </span>
                  </div>
                )}
                {selectedTruck?.driver_full_name && (
                  <p className="mt-1 text-sm text-muted-foreground">
                    Chauffeur habituel : {selectedTruck.driver_full_name}
                  </p>
                )}
              </div>

              <div>
                <Label className="label-base">Quantité estimée (L)</Label>
                <Input
                  type="number"
                  value={formData.fuel_quantity_estimated}
                  onChange={(e) =>
                    handleChange('fuel_quantity_estimated', e.target.value)
                  }
                  className="input-base"
                  data-testid="fuel-estimated-input"
                />
                {/* Pré-remplie par la citerne du camion, qui était affichée à
                    côté du sélecteur sans jamais servir de valeur. */}
                {selectedTruck && (
                  <p className="mt-1 text-sm text-muted-foreground">
                    Citerne : {formatVolume(selectedTruck.capacity_liters)}
                  </p>
                )}
              </div>

              <ChampsAvances
                titre="Plus de détails"
                consequence="Le chauffeur habituel du camion et le barème de la société s'appliquent si vous ne renseignez rien."
                valeurs={[
                  formData.driver_id && formData.driver_id !== 'none_selected'
                    ? formData.driver_id
                    : '',
                  formData.fuel_price_unit,
                  formData.return_date,
                ]}
              >
                <div className="space-y-1">
                  <Label className="label-base">Chauffeur</Label>
                  <SearchableSelect
                    className="input-base"
                    data-testid="driver-select"
                    placeholder="Chauffeur habituel du camion"
                    searchPlaceholder="Rechercher un chauffeur…"
                    emptyMessage="Aucun chauffeur trouvé"
                    value={formData.driver_id || ''}
                    onValueChange={(v) => handleChange('driver_id', v)}
                    options={driverOptions}
                  />
                </div>

                <div className="space-y-1">
                  <Label className="label-base">Prix unitaire (FCFA/L)</Label>
                  <Input
                    type="number"
                    value={formData.fuel_price_unit}
                    onChange={(e) => {
                      setPrixTouche(true);
                      handleChange('fuel_price_unit', e.target.value);
                    }}
                    className="input-base"
                    data-testid="fuel-price-input"
                  />
                  {/* `725` était écrit en dur ici. Le barème de la société
                      répond à la même question, et il change avec le marché. */}
                  {nomBareme ? (
                    <p className="text-xs text-muted-foreground">
                      Barème « {nomBareme} »
                    </p>
                  ) : aucunBareme ? (
                    <p className="text-xs text-muted-foreground">
                      Aucun barème ne couvre ce corridor : saisissez le prix.
                    </p>
                  ) : null}
                </div>

                <div className="space-y-1">
                  <Label className="label-base">Retour prévu</Label>
                  <Input
                    type="date"
                    value={formData.return_date}
                    onChange={(e) => handleChange('return_date', e.target.value)}
                    className="input-base"
                  />
                </div>
              </ChampsAvances>
            </div>

            <FormActions hint={truckReturned ? 'Le camion choisi est rendu à son propriétaire : choisissez-en un autre pour pouvoir enregistrer.' : undefined}>
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate('/trips')}
              >
                Annuler
              </Button>
              <Button
                type="submit"
                disabled={
                  isSubmitting ||
                  (!formData.operation_id && !formData.lot_id) ||
                  !formData.truck_id ||
                  truckReturned
                }
                data-testid="submit-trip-btn"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    Création...
                  </>
                ) : (
                  <>
                    <Check className="w-4 h-4 mr-2" />
                    Créer le trajet
                  </>
                )}
              </Button>
            </FormActions>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default NewTripPage;
